import { accentOf, applyBrandFonts, applyPalette, brandFontKind } from "@simpro/ui";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { salonProfileQuery } from "../api/salon.queries";

export interface SalonBrandingProps {
  children: ReactNode;
}

/** Sets `<meta name="theme-color">`, adding it if the page has none. */
function setThemeColour(colour: string) {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.append(meta);
  }
  meta.content = colour;
}

/**
 * Paints the page as the salon rather than as the platform (TDD-SA FR-SA-122).
 *
 * Wrapped around the whole app rather than used on the home page, because the colour has to be
 * right on every screen a customer can land on directly, including the one they reach from a
 * reminder message.
 *
 * It renders its children while the profile is still loading. A salon is not its colour, and
 * holding a blank screen until a stylesheet variable arrives would make a slow connection look like
 * a broken link.
 */
export function SalonBranding({ children }: SalonBrandingProps) {
  const { data } = useQuery(salonProfileQuery());
  // The colour theme sets the page's colours; with Organic Wellness the brand colour is the accent (FR-SA-198, FR-SA-204).
  const colour = data?.primaryColour;
  const theme = data?.colourTheme;
  const name = data?.displayName;
  const displayFont = data?.displayFont;
  const bodyFont = data?.bodyFont;

  useEffect(() => {
    if (!colour) return;
    return applyPalette(colour, document.documentElement, theme);
  }, [colour, theme]);

  // The salon's chosen typefaces (TDD-SA FR-SA-140), loaded without blocking the first paint.
  useEffect(() => {
    if (!displayFont || !bodyFont) return;
    const root = document.documentElement;
    const undoFonts = applyBrandFonts({ display: displayFont, body: bodyFont }, root);
    // A sans display face is set heavy and tight, a serif one regular (design note salon-booking-design-2.md).
    const kind = brandFontKind(displayFont);
    if (kind) root.dataset.displayKind = kind;
    return () => {
      undoFonts();
      delete root.dataset.displayKind;
    };
  }, [displayFont, bodyFont]);

  useEffect(() => {
    if (name) document.title = name;
  }, [name]);

  // The browser bar and, once installed, the app's title bar in the salon's colour (Phase 4 D6).
  useEffect(() => {
    if (colour) setThemeColour(accentOf(colour, theme));
  }, [colour, theme]);

  return <>{children}</>;
}
